import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import FRONTEND_CONFIG from "./frontend_config";
import { useNavigate } from "react-router-dom";

export default function CreateQuestion() {
  const navigate = useNavigate();

  // Custom Alert State
  const [alertData, setAlertData] = useState({ show: false, message: "" });

  const showCustomAlert = (msg) => {
    setAlertData({ show: true, message: msg });
    setTimeout(() => setAlertData({ show: false, message: "" }), 2000);
  };

  const [commonFields, setCommonFields] = useState({
    className: "",
    subject: "",
    topic: "",
    level: "",
  });

  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState(["", "", "", ""]); // 4 by default
  const [correctAnswer, setCorrectAnswer] = useState("");
  const [questionsList, setQuestionsList] = useState([]);
  const [loading, setLoading] = useState(false);

  // Get logged-in user from localStorage
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    const loggedUser = JSON.parse(localStorage.getItem("user"));
    const id = loggedUser?._id || loggedUser?.id;

    if (!id) {
      showCustomAlert("Please login first.");
      navigate("/login");
      return;
    }

    setUserId(id);
  }, [navigate]);

  const handleCommonChange = (e) => {
    setCommonFields({ ...commonFields, [e.target.name]: e.target.value });
  };

  const handleRemoveOption = (index) => {
    const updated = options.filter((_, i) => i !== index);
    setOptions(updated);

    if (options[index] === correctAnswer) {
      setCorrectAnswer("");
    }
  };

  const handleOptionChange = (index, value) => {
    const updated = [...options];
    updated[index] = value;
    setOptions(updated);
  };

  const handleAddOption = () => {
    if (options.length < 10) {
      setOptions([...options, ""]);
    } else {
      showCustomAlert("Maximum 10 options allowed!");
    }
  };

  const handleSaveQuestion = async () => {
    if (!userId) return;

    if (options.some((opt) => opt.trim() === "")) {
      showCustomAlert("Please fill all options before saving!");
      return;
    }

    if (!question.trim() || !correctAnswer.trim()) {
      showCustomAlert("Please enter the question and correct answer!");
      return;
    }

    const formattedOptions = options
      .filter((opt) => opt.trim() !== "")
      .map((opt) => ({ optionText: opt }));

    const payload = {
      questionText: question,
      options: formattedOptions,
      correctAnswer,
      marks: 1,
      subject: commonFields.subject,
      level: commonFields.level,
      chapter: commonFields.topic,
      class: commonFields.className,
      createdBy: userId,
    };

    try {
      setLoading(true);
      const response = await fetch(
        `${FRONTEND_CONFIG.API_URL}/api/question/add-question`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to save question");
      }

      showCustomAlert("✅ Question saved successfully!");

      const newQuestion = {
        ...commonFields,
        question,
        options: options.filter((opt) => opt.trim() !== ""),
        correctAnswer,
      };

      setQuestionsList([...questionsList, newQuestion]);
      setQuestion("");
      setOptions(["", "", "", ""]);
      setCorrectAnswer("");
    } catch (error) {
      console.error("Error saving question:", error);
      showCustomAlert("❌ Failed to save question: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    const formatted = questionsList.map((q) => ({
      className: q.className,
      subject: q.subject,
      topic: q.topic,
      level: q.level,
      question: q.question,
      options: q.options.join("|"),
      correctAnswer: q.correctAnswer,
    }));

    const worksheet = XLSX.utils.json_to_sheet(formatted);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Questions");

    XLSX.writeFile(workbook, "questions.xlsx");
  };

  const handleImportFile = async (e) => {
    if (!userId) return;

    const file = e.target.files[0];
    if (!file) return;

    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data);
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const jsonData = XLSX.utils.sheet_to_json(sheet);

    for (let row of jsonData) {
      const payload = {
        questionText: row.question,
        correctAnswer: row.correctAnswer,
        marks: 1,
        subject: row.subject,
        level: row.level,
        chapter: row.topic,
        class: row.className,
        options: row.options.split("|").map((o) => ({ optionText: o.trim() })),
        createdBy: userId,
      };

      await fetch(`${FRONTEND_CONFIG.API_URL}/api/question/add-question`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
    }

    showCustomAlert("✅ Import Completed Successfully!");
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col items-center py-6">
      {/* Custom Alert */}
      {alertData.show && (
  <div className="fixed inset-0 flex items-center justify-center z-50">
    <div className="bg-black text-white px-6 py-3 rounded-lg shadow-xl text-center animate-fadeIn">
      {alertData.message}
    </div>
  </div>
)}


      <div className="bg-white shadow-lg rounded-xl p-6 w-full max-w-3xl">
        <h2 className="text-2xl font-semibold text-center mb-4">
          Create New Question
        </h2>

        {/* Common fields */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block font-medium mb-1">Class</label>
            <input
              type="text"
              name="className"
              value={commonFields.className}
              onChange={handleCommonChange}
              className="w-full border rounded-lg p-2"
              placeholder="Enter class (e.g., 10th)"
            />
          </div>

          <div>
            <label className="block font-medium mb-1">Subject</label>
            <input
              type="text"
              name="subject"
              value={commonFields.subject}
              onChange={handleCommonChange}
              className="w-full border rounded-lg p-2"
              placeholder="Enter subject (e.g., Science)"
            />
          </div>

          <div>
            <label className="block font-medium mb-1">Topic</label>
            <input
              type="text"
              name="topic"
              value={commonFields.topic}
              onChange={handleCommonChange}
              className="w-full border rounded-lg p-2"
              placeholder="Enter topic"
            />
          </div>

          <div>
            <label className="block font-medium mb-1">Question Level</label>
            <div className="flex gap-3">
              {["Easy", "Medium", "Hard"].map((level) => (
                <label key={level} className="flex items-center gap-1">
                  <input
                    type="radio"
                    name="level"
                    value={level}
                    checked={commonFields.level === level}
                    onChange={handleCommonChange}
                  />
                  {level}
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Question */}
        <div className="mb-4">
          <label className="block font-medium mb-1">Question</label>
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            className="w-full border rounded-lg p-2"
            rows="3"
            placeholder="Enter your question here"
          ></textarea>
        </div>

        {/* Options */}
        <div className="mb-4">
          <label className="block font-medium mb-2">Options</label>
          {options.map((opt, index) => (
            <div key={index} className="flex items-center gap-2 mb-2">
              <input
                type="text"
                value={opt}
                onChange={(e) => handleOptionChange(index, e.target.value)}
                className="flex-1 border rounded-lg p-2"
                placeholder={`Option ${index + 1}`}
              />

              {options.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveOption(index)}
                  className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600"
                >
                  −
                </button>
              )}
            </div>
          ))}

          {options.length < 10 && (
            <button
              type="button"
              onClick={handleAddOption}
              className="bg-gray-200 px-3 py-1 rounded hover:bg-gray-300 text-sm"
            >
              + Add Option
            </button>
          )}
        </div>

        {/* Correct Answer */}
        <div className="mb-4">
          <label className="block font-medium mb-1">Correct Answer</label>

          <select
            value={correctAnswer}
            onChange={(e) => setCorrectAnswer(e.target.value)}
            className="w-full border rounded-lg p-2"
          >
            <option value="">-- Select Correct Answer --</option>

            {options
              .filter((opt) => opt.trim() !== "")
              .map((opt, index) => (
                <option key={index} value={opt}>
                  {opt}
                </option>
              ))}
          </select>
        </div>

        <button
          onClick={handleSaveQuestion}
          disabled={loading}
          className={`${
            loading ? "bg-blue-400" : "bg-blue-600 hover:bg-blue-700"
          } text-white px-6 py-2 rounded-lg w-full`}
        >
          {loading ? "Saving..." : "Save Question"}
        </button>
      </div>

      {/* Import / Export */}
      <div className="flex gap-4 mt-6">
        <input
          type="file"
          accept=".csv, .xlsx"
          onChange={handleImportFile}
          className="border p-2 rounded"
        />

        <button
          onClick={handleExport}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
        >
          Export Questions
        </button>
      </div>

      {/* Saved Questions */}
      {questionsList.length > 0 && (
        <div className="mt-8 w-full max-w-3xl">
          <h3 className="text-xl font-semibold mb-3">Saved Questions</h3>

          {questionsList.map((q, idx) => (
            <div
              key={idx}
              className="bg-white shadow rounded-lg p-4 mb-3 border border-gray-200"
            >
              <p className="font-semibold">
                Q{idx + 1}: {q.question}
              </p>

              <ul className="list-disc list-inside">
                {q.options.map((opt, i) => (
                  <li key={i}>{opt}</li>
                ))}
              </ul>

              <p className="text-green-700 mt-1">
                Correct Answer: <b>{q.correctAnswer}</b>
              </p>

              <p className="text-gray-500 text-sm mt-1">
                Class: {q.className} | Subject: {q.subject} | Topic: {q.topic} |
                Level: {q.level}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
